import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarCheck, Sparkles, Wine } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n, MONTH_NAMES, DOW_NAMES } from "@/lib/i18n";
import ReserveDateDialog from "@/components/ReserveDateDialog";
import { toISO, todayISO, buildMonthGrid } from "@/lib/calendar";

export default function AvailabilityCalendar({ whatsappNumber }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [reserveDate, setReserveDate] = useState(null);
  const navigate = useNavigate();
  const { t, locale } = useI18n();

  useEffect(() => {
    api.get("/events/calendar", { params: { year, month: month + 1 } })
      .then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [year, month]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    });
    return map;
  }, [events]);

  const todayStr = todayISO();
  const cells = buildMonthGrid(year, month);

  // Compute a summary of what parts of the day are blocked
  const dayStatus = (evs) => {
    if (!evs || evs.length === 0) return { free: true };
    const blocks = evs.map((e) => e.blocks_availability || "full_day");
    const publicEv = evs.find((e) => e.type === "public");
    const anyFull = blocks.includes("full_day") || blocks.includes("custom");
    const morning = anyFull || blocks.includes("morning");
    const evening = anyFull || blocks.includes("evening");
    const fullyBlocked = anyFull || (morning && evening);
    const partiallyBlocked = !fullyBlocked && (morning || evening);
    return { free: false, publicEv, morning, evening, fullyBlocked, partiallyBlocked, evs };
  };

  const navigateMonth = (delta) => {
    const next = new Date(year, month + delta, 1);
    setYear(next.getFullYear()); setMonth(next.getMonth());
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const iso = toISO(year, month, day);
    if (iso < todayStr) return;
    const evs = eventsByDate[iso];
    const status = dayStatus(evs);
    // Public event → navigate to it
    if (status.publicEv) return navigate(`/eventos/${status.publicEv.id}`);
    // If day is fully blocked → not clickable
    if (status.fullyBlocked) return;
    // Otherwise → open reserve dialog (works for free or partially blocked days)
    setReserveDate(iso);
  };

  return (
    <section
      id="disponibilidad"
      data-testid="calendar-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F2EDE5] aura-grain"
    >
      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        {/* Pitch text (left) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-2 lg:sticky lg:top-28"
        >
          <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-4">{t.calendar.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">{t.calendar.title}</h2>
          <h3 className="mt-6 font-display text-2xl md:text-3xl text-[#3e664c] italic">
            {t.calendar.pitchTitle}
          </h3>
          <p className="mt-4 text-[#5C544F] leading-relaxed max-w-md">{t.calendar.pitchDesc}</p>

          <ul className="mt-8 space-y-4 max-w-md">
            <li className="flex items-start gap-3">
              <CalendarCheck size={18} className="text-[#3e664c] mt-1 shrink-0" />
              <span className="text-sm text-[#2A2421]">{t.calendar.legendFree} → {t.calendar.tooltipFree}</span>
            </li>
            <li className="flex items-start gap-3">
              <Sparkles size={18} className="text-[#3e664c] mt-1 shrink-0" />
              <span className="text-sm text-[#2A2421]">{t.calendar.legendPublic}</span>
            </li>
            <li className="flex items-start gap-3">
              <Wine size={18} className="text-[#3e664c] mt-1 shrink-0" />
              <span className="text-sm text-[#2A2421]">{t.calendar.legendPrivate}</span>
            </li>
          </ul>
        </motion.div>

        {/* Calendar card (right, smaller) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-3 bg-white rounded-3xl border border-[#eae0df] p-5 md:p-7 shadow-[0_8px_30px_rgb(42,36,33,0.06)]"
          data-testid="calendar-card"
        >
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigateMonth(-1)}
              data-testid="calendar-prev-month"
              className="w-9 h-9 rounded-full border border-[#eae0df] flex items-center justify-center hover:border-[#3e664c] hover:text-[#3e664c] transition-colors"
              aria-label="prev"
            >
              <ChevronLeft size={16} />
            </button>
            <AnimatePresence mode="wait">
              <motion.div
                key={`${year}-${month}`}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35 }}
                className="text-center"
              >
                <h3 className="font-display text-2xl md:text-3xl tracking-tight" data-testid="calendar-month-title">
                  {MONTH_NAMES[locale][month]} <span className="text-[#3e664c]">{year}</span>
                </h3>
              </motion.div>
            </AnimatePresence>
            <button
              onClick={() => navigateMonth(1)}
              data-testid="calendar-next-month"
              className="w-9 h-9 rounded-full border border-[#eae0df] flex items-center justify-center hover:border-[#3e664c] hover:text-[#3e664c] transition-colors"
              aria-label="next"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {DOW_NAMES[locale].map((d) => (
              <div key={d} className="text-center text-[10px] uppercase tracking-[0.18em] text-[#5C544F]/70 py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1.5">
            {cells.map((day, idx) => {
              if (day === null) return <div key={`empty-${idx}`} className="aspect-square" />;
              const iso = toISO(year, month, day);
              const evs = eventsByDate[iso];
              const status = dayStatus(evs);
              const isPast = iso < todayStr;
              const isToday = iso === todayStr;

              // Determine visual state
              let stateKey = "free";
              if (status.publicEv) stateKey = "public";
              else if (status.fullyBlocked) stateKey = "wine";
              else if (status.partiallyBlocked) stateKey = "partial";

              const interactable = !isPast && stateKey !== "wine";

              const baseClass = isPast
                ? "bg-[#fdfcf9] text-[#5C544F]/40 border border-[#eae0df]/60"
                : stateKey === "free"
                ? "bg-[#fdfcf9] text-[#2A2421] border border-[#eae0df] hover:bg-[#E2C9A0]/25 hover:border-[#3e664c]/40"
                : stateKey === "partial"
                ? "bg-[#fdfcf9] text-[#2A2421] border border-[#eae0df] hover:border-[#3e664c]/60"
                : stateKey === "wine"
                ? "bg-[#450905] text-[#fdfcf9] border border-[#2c0603]"
                : "bg-gradient-to-br from-[#E2C9A0] to-[#C9A24B] text-[#2e5949] border border-[#C9A24B] shadow-[0_4px_15px_rgb(201,162,75,0.35)] hover:from-[#E8D2A8] hover:to-[#D4AD55]";

              const tooltip = status.publicEv
                ? status.publicEv.title
                : stateKey === "wine"
                ? t.calendar.tooltipPrivate
                : stateKey === "partial"
                ? (status.morning ? "Matí ocupat · nit lliure" : "Nit ocupada · matí lliure")
                : t.calendar.tooltipFree;

              return (
                <button
                  key={iso}
                  onClick={() => handleDayClick(day)}
                  disabled={!interactable}
                  data-testid={`calendar-day-${iso}`}
                  data-state={isPast ? "past" : stateKey}
                  title={tooltip}
                  className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-center transition-all overflow-hidden
                    ${isPast ? "opacity-50 cursor-not-allowed" : interactable ? "cursor-pointer hover:-translate-y-0.5" : "cursor-not-allowed"}
                    ${isToday ? "ring-2 ring-[#3e664c] ring-offset-1 ring-offset-white" : ""}
                    ${baseClass}`}
                >
                  {/* Partial-block visual: half-fills for morning/evening */}
                  {stateKey === "partial" && status.morning && (
                    <span className="absolute inset-x-0 top-0 h-1/2 bg-[#450905]/85 pointer-events-none" />
                  )}
                  {stateKey === "partial" && status.evening && (
                    <span className="absolute inset-x-0 bottom-0 h-1/2 bg-[#450905]/85 pointer-events-none" />
                  )}
                  <span className={`font-display text-lg md:text-xl leading-none relative z-[1] ${
                    stateKey === "partial" ? "mix-blend-difference text-white" : ""
                  }`}>
                    {day}
                  </span>
                  {stateKey === "public" && (
                    <>
                      <Sparkles size={10} className="absolute top-1 right-1 text-[#2e5949]/70" />
                      <span className="mt-0.5 text-[8px] uppercase tracking-[0.18em] font-medium leading-none hidden sm:block">
                        Event
                      </span>
                    </>
                  )}
                  {stateKey === "wine" && (
                    <span className="mt-1 w-3 h-0.5 rounded-full bg-[#fdfcf9]/70" />
                  )}
                  {stateKey === "free" && !isPast && (
                    <span className="mt-1 w-1 h-1 rounded-full bg-[#6F8C76]" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-5 text-[10px] uppercase tracking-[0.2em] text-[#5C544F]">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#fdfcf9] border border-[#eae0df] inline-flex items-center justify-center">
                <span className="w-1 h-1 rounded-full bg-[#6F8C76]" />
              </span>
              <span data-testid="legend-free">{t.calendar.legendFree}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-md bg-[#450905]" />
              <span data-testid="legend-private">{t.calendar.legendPrivate}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-md bg-gradient-to-br from-[#E2C9A0] to-[#C9A24B] shadow-[0_2px_6px_rgb(201,162,75,0.4)]" />
              <span data-testid="legend-public">{t.calendar.legendPublic}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-md bg-[#fdfcf9] border border-[#eae0df] relative overflow-hidden">
                <span className="absolute inset-x-0 top-0 h-1/2 bg-[#450905]/85" />
              </span>
              <span data-testid="legend-partial">Parcialment ocupat</span>
            </div>
          </div>
        </motion.div>
      </div>

      <ReserveDateDialog
        open={!!reserveDate}
        date={reserveDate}
        whatsappNumber={whatsappNumber}
        onClose={() => setReserveDate(null)}
      />
    </section>
  );
}
